import type { MarketDataErrorKind } from '../../context/MarketDataContext.utils';
import styles from './ErrorBanner.module.css';

interface ErrorBannerProps {
  /** Typed kind from MarketDataContext; drives the friendly copy. */
  kind: MarketDataErrorKind;
  /** Raw error message (kept for diagnostics; not rendered to users). */
  message?: string;
  /** Show the Retry button + wire it up. */
  onRetry?: () => void;
  /** Show the dismiss "x" button. */
  onDismiss?: () => void;
}

interface CopyForKind {
  title: string;
  body: string;
  /** Some errors auto-retry server-side; we hide manual retry in those cases. */
  showRetry: boolean;
}

const COPY: Record<MarketDataErrorKind, CopyForKind> = {
  'rate-limited': {
    title: 'Slow down a moment',
    body: "We're being rate-limited by Google Sheets. Trying again shortly.",
    showRetry: true,
  },
  unauthorized: {
    title: 'Map data is misconfigured',
    body: 'The market map data is misconfigured. Please contact the organizer.',
    showRetry: false,
  },
  unavailable: {
    title: 'Sheets is temporarily unavailable',
    body: "Google Sheets is temporarily unavailable. You're seeing the most recent cached data.",
    showRetry: true,
  },
  malformed: {
    title: 'Unexpected data format',
    body: 'The market data has an unexpected format. The organizer may need to update the spreadsheet.',
    showRetry: true,
  },
  unknown: {
    title: 'Could not load latest data',
    body: 'Something went wrong loading the latest data.',
    showRetry: true,
  },
};

/**
 * Non-blocking banner for data-load failures. Mount when
 * `state.error && state.errorKind` is truthy. Safe to mount even when
 * cached data is also rendering — the banner sits above the content.
 *
 * Wire into AppShell at the top of the main content area, e.g.:
 *
 *   {errorKind && (
 *     <ErrorBanner
 *       kind={errorKind}
 *       message={error ?? undefined}
 *       onRetry={refresh}
 *       onDismiss={dismissError}
 *     />
 *   )}
 */
export function ErrorBanner({ kind, message, onRetry, onDismiss }: ErrorBannerProps) {
  const copy = COPY[kind] ?? COPY.unknown;
  const showRetry = copy.showRetry && Boolean(onRetry);

  return (
    <div className={styles.root} role="alert" aria-live="assertive">
      <div className={styles.icon} aria-hidden="true">!</div>
      <div className={styles.content}>
        <p className={styles.title}>{copy.title}</p>
        <p className={styles.body}>{copy.body}</p>
        {message && (
          <p className={styles.detail} title={message}>
            {message}
          </p>
        )}
      </div>
      <div className={styles.actions}>
        {showRetry && (
          <button
            type="button"
            className={styles.retryBtn}
            onClick={onRetry}
          >
            Retry
          </button>
        )}
        {onDismiss && (
          <button
            type="button"
            className={styles.dismissBtn}
            onClick={onDismiss}
            aria-label="Dismiss error"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <path
                d="M3 3 L11 11 M11 3 L3 11"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
